import React from 'react';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import './MaternityPackagesManagement.less';

import i2Math from '@/utils/i2Math';
import { renderTextStr, renderFormattedInt } from '@/utils/renderUtils';

class MaternityPackagesManagement extends React.Component {
  constructor(props) {
    super(props);
    this.chartRef = React.createRef();
    this.chartInstance = null;
    this.tooltipObserver = null;
    this.initChartTimer = null; // 延迟初始化定时器
    this.isInitializing = false; // 初始化标志，防止重复初始化
    this.lastInitKey = null; // 上次初始化的 key，用于判断是否需要重新初始化
    this.updateTimer = null; // 防抖定时器
    this.state = {
      /** 近12月曲线（优先 Redux packageByYear；不足则接口补全） */
      mergedChartStoreObj: null
    };
  }

  /** 将接口中的 month（'01' / 10 等）转为 1–12 的月份序号，非法则返回 -1 */
  monthToIndex1To12 = (month) => {
    const n = parseInt(String(month), 10);
    if (!Number.isFinite(n)) return -1;
    if (n < 1 || n > 12) return -1;
    return n;
  };

  /**
   * 无 store_id 时：总额用 package.total_sum；折线图为全部门店按月汇总（store_map 内 list 的 sum 相加）
   */
  buildAggregatedStoreView = (pkg) => {
    const { store_map: storeMap, total_sum: totalSum } = pkg || {};
    if (!storeMap || typeof storeMap !== 'object') {
      return null;
    }
    const monthly = Array.from({ length: 12 }, () => 0);
    Object.values(storeMap).forEach((store) => {
      (store.list || []).forEach((item) => {
        const m = this.monthToIndex1To12(item.month);
        if (m === -1) return;
        monthly[m - 1] = i2Math.i2Add(monthly[m - 1], Number(item.sum) || 0);
      });
    });
    const list = monthly.map((sum, i) => ({ month: i + 1, sum }));
    let total = totalSum;
    if (total == null) {
      total = list.reduce((acc, row) => i2Math.i2Add(acc, Number(row.sum) || 0), 0);
    }
    return { total, list };
  };

  /** 有 store_id：单店；无 store_id：全部门店汇总（与 RevenueSummaryCard 一致） */
  getStoreObj = () => {
    const { erpStoreAnnualStatisticsObj } = this.props;
    const pkg = erpStoreAnnualStatisticsObj?.package;
    return this.extractStoreViewFromPackage(pkg);
  };

  /** 从已处理的 package 对象解析单店或汇总视图（可传入不同年份的 package） */
  extractStoreViewFromPackage = (pkg) => {
    if (!pkg) {
      return null;
    }
    const { queryManager } = this.props;
    const storeId = queryManager?.currentQuery?.store_id;
    const hasStoreId = storeId !== undefined && storeId !== null && String(storeId).trim() !== '';

    if (hasStoreId) {
      const id = String(storeId);
      return pkg.store_map?.[id] || null;
    }

    return this.buildAggregatedStoreView(pkg);
  };

  /**
   * 将各自然年的门店/汇总视图压平为 year-month → sum，再生成以 anchorEnd 所在月为终点的连续 12 个月
   *（例：今日 2026-05 → 25年6 … 26年5 对应 2025-06 … 2026-05）
   */
  buildRolling12FromYearStoreMap = (yearToStoreObj, anchorEnd) => {
    const yearMonthToSum = new Map();
    const ingest = (storeObj, calendarYear) => {
      if (!storeObj?.list?.length) {
        return;
      }
      storeObj.list.forEach((row) => {
        const m = this.monthToIndex1To12(row.month);
        if (m === -1) {
          return;
        }
        const key = `${calendarYear}-${m}`;
        yearMonthToSum.set(
          key,
          i2Math.i2Add(yearMonthToSum.get(key) || 0, Number(row.sum) || 0)
        );
      });
    };
    Object.keys(yearToStoreObj).forEach((yStr) => {
      ingest(yearToStoreObj[yStr], parseInt(yStr, 10));
    });

    const end = anchorEnd.startOf('month');
    const list = [];
    for (let i = 11; i >= 0; i -= 1) {
      const d = end.subtract(i, 'month');
      const y = d.year();
      const mo = d.month() + 1;
      const sum = yearMonthToSum.get(`${y}-${mo}`) ?? 0;
      list.push({
        month: mo,
        year: y,
        sum,
        label: `${String(y).slice(2)}年${mo}`
      });
    }
    const total = list.reduce((acc, row) => i2Math.i2Add(acc, Number(row.sum) || 0), 0);
    return { total, list };
  };

  /** 近12个月窗口 [end-11, end] 涉及的所有历年份（通常 1～2 个） */
  getYearsInRollingWindow = (anchorEnd) => {
    const end = anchorEnd.startOf('month');
    const years = new Set();
    for (let i = 11; i >= 0; i -= 1) {
      years.add(end.subtract(i, 'month').year());
    }
    return [...years].sort((a, b) => a - b);
  };

  /**
   * 用 Redux 里按年缓存的 package（packageByYear）拼近12月；锚点为 query.date（与指挥中心筛选一致）
   */
  tryBuildMergedFromPackageByYear = () => {
    const { app, erpStoreAnnualStatisticsObj, queryManager } = this.props;
    const dateStr = queryManager?.currentQuery?.date;
    const anchorEnd =
      dateStr && dayjs(dateStr).isValid() ? dayjs(dateStr) : dayjs();
    const yearList = this.getYearsInRollingWindow(anchorEnd);
    const byYear = erpStoreAnnualStatisticsObj?.packageByYear;
    if (!byYear || typeof byYear !== 'object') {
      return null;
    }
    const yearToStoreObj = {};
    for (let i = 0; i < yearList.length; i += 1) {
      const y = yearList[i];
      const pkg = byYear[String(y)];
      if (!pkg) {
        return null;
      }
      const pkgQuery = pkg.query || {};
      const user = app?.user ?? {};
      if (
        String(pkgQuery.userId ?? '') !== String(user.oa_id ?? '') ||
        String(pkgQuery.companyId ?? '') !== String(user.com_id ?? '')
      ) {
        return null;
      }
      const storeObj = this.extractStoreViewFromPackage(pkg);
      if (!storeObj?.list?.length) {
        return null;
      }
      yearToStoreObj[y] = storeObj;
    }
    return this.buildRolling12FromYearStoreMap(yearToStoreObj, anchorEnd);
  };

  /** 优先 Redux 两年数据；不齐则走接口（锚点均为 query.date） */
  syncRollingChart = () => {
    const built = this.tryBuildMergedFromPackageByYear();
    if (built?.list?.length) {
      const curSig = this.getChartDataSignature(this.state.mergedChartStoreObj);
      const nextSig = this.getChartDataSignature(built);
      if (curSig !== nextSig) {
        this.setState({ mergedChartStoreObj: built });
      }
      return;
    }
    if (this.state.mergedChartStoreObj) {
      this.setState({ mergedChartStoreObj: null });
    } else {
      this.disposeChart();
    }
  };

  /** 曲线只使用近12月合并数据；未 ready 时不回退年度主槽，避免先画旧图再重绘 */
  getChartStoreObj = () => {
    const { mergedChartStoreObj } = this.state;
    if (mergedChartStoreObj?.list?.length) {
      return mergedChartStoreObj;
    }
    return null;
  };

  getChartDataSignature = (storeObj) => {
    if (!storeObj?.list?.length) {
      return '';
    }
    const listSig = storeObj.list
      .map((row) => (row.year != null ? `${row.year}_` : '') + `${row.month}_${row.sum}`)
      .join(',');
    return `${storeObj.total}_${listSig}`;
  };

  // 初始化图表
  initChart = () => {
    if (!this.chartRef.current) return;

    const storeObj = this.getChartStoreObj();
    if (!storeObj?.list?.length) {
      this.disposeChart();
      return;
    }

    const { queryManager } = this.props;
    const sid = queryManager?.currentQuery?.store_id;
    const modePrefix =
      sid !== undefined && sid !== null && String(sid).trim() !== '' ? String(sid) : 'ALL';
    const initKey = `${modePrefix}_${this.getChartDataSignature(storeObj)}`;
    
    // 如果数据没有变化且图表已存在，只更新数据，不重新初始化
    if (this.chartInstance && this.lastInitKey === initKey) {
      this.updateChartData(storeObj);
      return;
    }

    // 如果正在初始化，取消之前的初始化
    if (this.isInitializing) {
      if (this.initChartTimer) {
        cancelAnimationFrame(this.initChartTimer);
        this.initChartTimer = null;
      }
      return;
    }

    // 检查容器高度，如果为 0 则延迟初始化
    const containerHeight = this.chartRef.current.offsetHeight;
    if (containerHeight === 0) {
      this.isInitializing = true;
      this.initChartTimer = requestAnimationFrame(() => {
        this.initChartTimer = null;
        this.isInitializing = false;
        if (this.chartRef.current?.offsetHeight > 0) {
          this.initChart();
        }
      });
      return;
    }

    this.isInitializing = true;
    this.lastInitKey = initKey;

    // 清理旧的 observer
    if (this.tooltipObserver) {
      this.tooltipObserver.disconnect();
      this.tooltipObserver = null;
    }

    // 如果图表已存在，先销毁
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    this.chartInstance = echarts.init(this.chartRef.current);

    const axisLabelFontSize = Math.max(8, Math.min(12, containerHeight * 0.15));
    const tooltipFontSize = Math.max(10, Math.min(14, containerHeight * 0.2));

    const { amounts, labels } = storeObj.list.reduce((acc, item) => {
      acc.amounts.push(item.sum || 0);
      acc.labels.push(item.label ? item.label : `${item.month}月`);
      return acc;
    }, { amounts: [], labels: [] });

    const option = {
      backgroundColor: 'transparent',
      animation: true,
      animationDuration: 1200,
      animationEasing: 'cubicOut',
      animationDurationUpdate: 900,
      animationEasingUpdate: 'cubicInOut',
      grid: {
        left: '3%',
        right: '3%',
        top: '5%',
        bottom: '5%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#20b2aa',
        borderWidth: 1,
        textStyle: {
          color: '#ffffff',
          fontSize: tooltipFontSize
        },
        z: 100000,
        extraCssText: 'z-index: 100000 !important; position: fixed !important;',
        appendToBody: true,
        formatter: (params) => {
          const param = params[0];
          return `${param.name}<br/>${param.seriesName}: ${renderFormattedInt(param.value)}元`;
        }
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: {
          color: '#8c8c8c',
          fontSize: axisLabelFontSize,
          interval: 0,
          rotate: labels.length > 8 ? 35 : 0
        },
        axisLine: {
          lineStyle: {
            color: '#e8e8e8'
          }
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#8c8c8c',
          fontSize: axisLabelFontSize,
          formatter: (value) => {
            if (value >= 10000) {
              return (value / 10000).toFixed(1) + '万';
            }
            return value;
          }
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: '月套餐',
          type: 'line',
          smooth: true,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(32, 178, 170, 0.3)' },
                { offset: 1, color: 'rgba(32, 178, 170, 0.05)' }
              ]
            }
          },
          lineStyle: {
            color: '#20b2aa',
            width: 2,
            shadowBlur: 10,
            shadowColor: 'rgba(32, 178, 170, 0.45)',
            shadowOffsetY: 0
          },
          itemStyle: {
            color: '#20b2aa'
          },
          data: amounts,
          emphasis: {
            focus: 'series',
            lineStyle: {
              width: 3,
              shadowBlur: 16,
              shadowColor: 'rgba(32, 178, 170, 0.65)'
            },
            itemStyle: {
              borderWidth: 2,
              borderColor: '#fff'
            }
          }
        }
      ]
    };

    this.chartInstance.setOption(option, {
      renderer: 'canvas'
    });

    // 使用 MutationObserver 监听 tooltip 元素的创建
    this.tooltipObserver = new MutationObserver(() => {
      const tooltipElements = document.querySelectorAll('.echarts-tooltip');
      tooltipElements.forEach(el => {
        if (el instanceof HTMLElement) {
          if (el.parentElement !== document.body) {
            document.body.appendChild(el);
          }
          el.style.zIndex = '100000';
          el.style.position = 'fixed';
          el.style.pointerEvents = 'none';
        }
      });
    });

    this.tooltipObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.isInitializing = false;
  };

  disposeChart = () => {
    if (this.tooltipObserver) {
      this.tooltipObserver.disconnect();
      this.tooltipObserver = null;
    }
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    this.lastInitKey = null;
  };

  // 更新图表数据（不重新初始化）
  updateChartData = (storeObj) => {
    if (!this.chartInstance || !storeObj?.list?.length) {
      return;
    }

    // 提取图表数据
    const { amounts, labels } = storeObj.list.reduce((acc, item) => {
      acc.amounts.push(item.sum || 0);
      acc.labels.push(item.label ? item.label : `${item.month}月`);
      return acc;
    }, { amounts: [], labels: [] });

    // 只更新数据，不重新初始化
    this.chartInstance.setOption({
      xAxis: {
        data: labels
      },
      series: [{
        data: amounts
      }]
    }, { notMerge: false, lazyUpdate: false });
  };

  componentDidMount() {
    this.syncRollingChart();
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps, prevState) {
    const { erpStoreAnnualStatisticsObj, queryManager, app } = this.props;

    // 近12月数据 ready 后再初始化图表；不 ready 时销毁旧图，避免旧图闪一下
    if (prevState.mergedChartStoreObj !== this.state.mergedChartStoreObj) {
      this.lastInitKey = null;
      if (!this.state.mergedChartStoreObj?.list?.length) {
        this.disposeChart();
      } else if (!this.isInitializing) {
        if (this.updateTimer) {
          clearTimeout(this.updateTimer);
        }
        this.updateTimer = setTimeout(() => {
          this.updateTimer = null;
          this.initChart();
        }, 50);
      }
    }

    const prevSid = prevProps.queryManager?.currentQuery?.store_id;
    const curSid = queryManager?.currentQuery?.store_id;
    const prevHasStore =
      prevSid !== undefined && prevSid !== null && String(prevSid).trim() !== '';
    const curHasStore =
      curSid !== undefined && curSid !== null && String(curSid).trim() !== '';

    const storeIdChanged =
      prevHasStore !== curHasStore ||
      (prevHasStore && curHasStore && String(prevSid) !== String(curSid));

    const prevUser = prevProps.app?.user ?? {};
    const curUser = app?.user ?? {};
    const userChanged =
      prevUser.oa_id !== curUser.oa_id || prevUser.com_id !== curUser.com_id;

    const prevDate = prevProps.queryManager?.currentQuery?.date;
    const curDate = queryManager?.currentQuery?.date;

    const prevPkgBy = prevProps.erpStoreAnnualStatisticsObj?.packageByYear;
    const curPkgBy = erpStoreAnnualStatisticsObj?.packageByYear;
    const packageByYearChanged = prevPkgBy !== curPkgBy;

    /** Redux 按年缓存更新、筛选日期/门店/账号变化时，重算近12月（优先 packageByYear） */
    if (
      storeIdChanged ||
      userChanged ||
      prevDate !== curDate ||
      packageByYearChanged
    ) {
      this.syncRollingChart();
    }

  }

  componentWillUnmount() {
    // 清理定时器
    if (this.initChartTimer) {
      cancelAnimationFrame(this.initChartTimer);
      this.initChartTimer = null;
    }
    
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    
    // 清理 MutationObserver
    if (this.tooltipObserver) {
      this.tooltipObserver.disconnect();
      this.tooltipObserver = null;
    }
    
    // 销毁图表
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  };

  render() {
    const { packageSummaryObj, queryManager, monthRevenueData, storeDataByDay } = this.props;
    
    if (!packageSummaryObj?.data || !queryManager?.currentQuery) {
      return null;
    }

    const packageSummaryData = packageSummaryObj.data;
    const storeObj = this.getStoreObj() || {};
    const chartReady = Boolean(this.state.mergedChartStoreObj?.list?.length);
    // 使用父组件预计算的月份收入数据（package 对应产康套餐）
    const monthSignedOrderRevenue = monthRevenueData?.package || 0;
    // 使用父组件预计算的门店日数据
    const storeDataByDayObj = storeDataByDay || {};

    return (
    <div className="maternity-packages-management">
      {/* 顶部：日套餐签单和月套餐签单 */}
      <div className="section-top">
        <div className="section-half left">
          <div className="section-title">日套餐签单</div>
          <div className="amount-large">
            <span className="number">{renderFormattedInt(storeDataByDayObj.sales)}</span>
            <span className="unit">元</span>
          </div>
          <div className="amount-small">{renderTextStr(packageSummaryData.todaySignedOrderCount)} 单</div>
        </div>
        <div className="divider"></div>
        <div className="section-half right">
          <div className="section-title">月套餐签单</div>
          <div className="amount-large">
            <span className="number">{renderFormattedInt(monthSignedOrderRevenue)}</span>
            <span className="unit">元</span>
          </div>
          <div className="amount-small">{renderTextStr(packageSummaryData.monthSignedOrderCount)} 单</div>
        </div>
      </div>

      {/* 中间：近12月月子套餐签单金额（跨年由去年+今年接口拼接） */}
      <div className="section-middle">
        <div className="section-header">
          <div className="section-title-dark">近12月月子套餐签单金额</div>
        </div>
        <div className="chart-container">
          {chartReady ? (
            <div ref={this.chartRef} className="chart-echarts"></div>
          ) : (
            <div className="chart-placeholder">数据加载中...</div>
          )}
        </div>
        <div className="metrics">
          <div className="metric-primary">
            <span className="number">{renderFormattedInt(storeObj.total)}</span>
            <span className="unit">元</span>
          </div>
        </div>
      </div>
    </div>
    );
  }
}

export default MaternityPackagesManagement;
